/**
 * 기사앱 설치·알림 안내 팝업(2026-09-27 way — 「설정 전까지 계속」, DYOPS·YDYOPS InstallPrompt·PushPermissionPrompt 사례).
 *
 * 한 번에 하나만 띄운다(60대 기사 — 겹친 배너 둘은 읽히지 않는다): ① 앱 설치 → ② 알림 허용.
 * - 「나중에」는 **이번에 연 동안만** 닫는다(sessionStorage). 앱을 다시 열면 또 뜬다 — 설치·허용할 때까지.
 * - 설치됐으면(홈 화면 앱으로 실행 / 이 폰에 설치 확인) ①은 안 뜬다. 알림이 허용됐으면 ②는 안 뜨고
 *   앱을 열 때마다 토큰만 조용히 다시 올린다.
 * - 알림을 「차단」했으면 권한 창을 다시 띄울 수 없다(브라우저 규칙) → 설정에서 켜는 방법을 보여 준다.
 * - 로그인 전에는 ①만(설치부터 하면 아이폰에서 로그인을 두 번 안 해도 된다). ②는 로그인 뒤.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  enableDriverPush,
  hasInstallPrompt,
  isInstalledElsewhere,
  isIos,
  copyText,
  isInAppBrowser,
  isKakaoInApp,
  isNativeApp,
  isStandalone,
  openInExternalBrowser,
  pushPermission,
  pushPermissionAsync,
  registerDriverSw,
  triggerInstall,
  type PushState,
} from '../lib/driverPush';

const LATER_KEY = (k: 'install' | 'push') => `businweb.driver.later.${k}`;

function laterToday(k: 'install' | 'push'): boolean {
  try {
    return sessionStorage.getItem(LATER_KEY(k)) === '1';
  } catch {
    return false;
  }
}

export default function AppPrompts({ loggedIn }: { loggedIn: boolean }) {
  const [installed, setInstalled] = useState(() => isStandalone());
  const [installable, setInstallable] = useState(() => hasInstallPrompt());
  const [perm, setPerm] = useState<PushState>(() => pushPermission());
  const [later, setLater] = useState({ install: laterToday('install'), push: laterToday('push') });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void registerDriverSw();
    void isInstalledElsewhere().then((v) => v && setInstalled(true));
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => setInstalled(true);
    // 스토어 앱은 권한을 비동기로만 안다(처음 값 'unsupported' → 여기서 채운다).
    void pushPermissionAsync().then(setPerm);
    // 설정 앱에서 알림을 켜고 돌아왔을 때 다시 센다.
    const onVisible = () => document.visibilityState === 'visible' && void pushPermissionAsync().then(setPerm);
    window.addEventListener('driver:installable', onInstallable);
    window.addEventListener('driver:installed', onInstalled);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('driver:installable', onInstallable);
      window.removeEventListener('driver:installed', onInstalled);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // 이미 허용된 폰 — 앱을 열 때마다 토큰만 조용히 다시 올린다.
  useEffect(() => {
    if (loggedIn && perm === 'granted') void enableDriverPush(false);
  }, [loggedIn, perm]);

  function closeForNow(k: 'install' | 'push') {
    try {
      sessionStorage.setItem(LATER_KEY(k), '1');
    } catch {
      /* 저장 못 해도 이번 화면에선 닫힌다 */
    }
    setLater((s) => ({ ...s, [k]: true }));
    setErr(null);
  }

  // 카카오톡·구 버스인 기사앱 공지 링크 등 다른 앱 안 화면 — 설치·알림이 안 된다(2026-10-07 way).
  const inApp = isInAppBrowser();
  const kakao = isKakaoInApp();
  const ios = isIos();

  // ① 설치
  if (!installed && !later.install) {
    if (kakao) {
      return (
        <Sheet icon="📲" title="카카오톡 안에서는 앱 설치가 안 됩니다" onLater={() => closeForNow('install')}>
          <p>아래 버튼을 눌러 휴대폰 기본 인터넷(크롬·삼성 인터넷·사파리)으로 여세요. 거기서 앱을 설치할 수 있습니다.</p>
          <Primary onClick={openInExternalBrowser}>기본 인터넷으로 열기</Primary>
          <CopyAddress />
        </Sheet>
      );
    }
    // 구 버스인 기사앱 공지 링크 등 — 웹에서 바깥 브라우저를 열 길이 없다 → 주소 복사가 주 버튼.
    // 🔴 10-07 실기기: intent:// · 새 창(target=_blank) · 다운로드 넘김 · sms: · googlechrome:// · market: · tel: 전부 안 됨(그 WebView 가 전부 막는다).
    //    다시 시험하지 말 것 — 바깥으로 보내려면 링크를 카톡(알림톡)으로 보낸다.
    if (inApp) {
      return (
        <Sheet icon="📲" title="이 화면에서는 앱 설치가 안 됩니다" onLater={() => closeForNow('install')}>
          <p>휴대폰 번호를 넣고 <b>설치 문자 받기</b>를 누르세요. 문자로 온 주소를 누르면 휴대폰 인터넷으로 열리고, 거기서 앱을 설치할 수 있습니다.</p>
          <InstallSms />
          <p className="mt-4 font-semibold">문자가 안 오면</p>
          <ol className="mt-1 list-decimal space-y-1 pl-6 text-base">
            <li>아래 <b>주소 복사</b>를 누릅니다.</li>
            <li>휴대폰의 <b>크롬</b>(또는 삼성 인터넷·사파리)을 엽니다.</li>
            <li>맨 위 주소창을 길게 눌러 <b>붙여넣기</b> 하고 이동합니다.</li>
          </ol>
          <CopyAddress />
        </Sheet>
      );
    }
    if (ios) {
      return (
        <Sheet icon="📲" title="기사앱을 홈 화면에 추가하세요" onLater={() => closeForNow('install')}>
          <ol className="list-decimal space-y-2 pl-6">
            <li>
              사파리 아래(또는 위)의 <b>공유 버튼</b>(네모에 위쪽 화살표 ⬆)을 누릅니다.
            </li>
            <li>
              목록을 올려 <b>홈 화면에 추가</b>를 누릅니다.
            </li>
            <li>
              오른쪽 위 <b>추가</b>를 누릅니다.
            </li>
          </ol>
          <p className="mt-3 text-ink-soft">
            홈 화면에 생긴 <b>기사앱</b> 아이콘으로 들어오면 알림도 받을 수 있습니다. 처음 한 번만 다시 로그인해 주세요.
          </p>
        </Sheet>
      );
    }
    return (
      <Sheet icon="📲" title="기사앱을 휴대폰에 설치하세요" onLater={() => closeForNow('install')}>
        <p>설치하면 홈 화면 아이콘으로 바로 들어오고, 새 배차·공지 알림을 받을 수 있습니다.</p>
        {installable ? (
          <Primary
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await triggerInstall();
                if (r === 'accepted') setInstalled(true);
                else setInstallable(hasInstallPrompt());
              } finally {
                setBusy(false);
              }
            }}
          >
            앱 설치
          </Primary>
        ) : (
          <p className="mt-3 rounded-xl bg-canvas p-4">
            화면 오른쪽 위 <b>점 세 개(⋮)</b> 메뉴 → <b>앱 설치</b> 또는 <b>홈 화면에 추가</b>를 누르세요.
          </p>
        )}
      </Sheet>
    );
  }

  // ② 알림 — 로그인 뒤. 카카오톡 안·설치 전 아이폰은 ①이 이미 방법을 알려 준다(여기선 못 켠다).
  if (!loggedIn || later.push || inApp || (ios && !isStandalone())) return null;
  if (done) {
    return (
      <Sheet icon="✅" title="알림이 켜졌습니다" onLater={() => setDone(false)} laterLabel="확인">
        <p>새 배차·공지가 올라오면 휴대폰으로 알려 드립니다.</p>
      </Sheet>
    );
  }
  if (perm === 'default') {
    return (
      <Sheet icon="🔔" title="알림을 켜 주세요" onLater={() => closeForNow('push')}>
        <p>새 배차·공지가 올라오면 휴대폰으로 바로 알려 드립니다.</p>
        <p className="mt-1 text-ink-soft">
          다음 창에서 <b>허용</b>을 눌러 주세요.
        </p>
        {err && <p className="mt-3 font-semibold text-red-700">{err}</p>}
        <Primary
          busy={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              const r = await enableDriverPush(true);
              setPerm(await pushPermissionAsync());
              if (r.ok) setDone(true);
              else setErr(r.reason);
            } finally {
              setBusy(false);
            }
          }}
        >
          알림 허용
        </Primary>
      </Sheet>
    );
  }
  if (perm === 'denied') {
    return (
      <Sheet icon="🔕" title="알림이 꺼져 있습니다" onLater={() => closeForNow('push')}>
        <p>새 배차·공지를 받으려면 알림을 켜 주세요.</p>
        <div className="mt-3 rounded-xl bg-canvas p-4">
          {ios ? (
            <p>
              아이폰 <b>설정</b> → <b>알림</b> → <b>{isNativeApp() ? '동영관광 기사앱' : '기사앱'}</b> → <b>알림 허용</b>을 켭니다.
            </p>
          ) : isStandalone() ? (
            <p>
              홈 화면의 <b>기사앱</b> 아이콘을 길게 누름 → <b>앱 정보</b>(ⓘ) → <b>알림</b> → 켭니다.
            </p>
          ) : (
            <p>
              주소창 왼쪽 <b>아이콘</b>을 누름 → <b>권한</b> → <b>알림</b> → <b>허용</b>으로 바꿉니다.
            </p>
          )}
        </div>
        <Primary onClick={() => void pushPermissionAsync().then(setPerm)}>켰습니다 · 다시 확인</Primary>
      </Sheet>
    );
  }
  return null;
}

function Sheet({
  icon,
  title,
  children,
  onLater,
  laterLabel = '나중에',
}: {
  icon: string;
  title: string;
  children: ReactNode;
  onLater: () => void;
  laterLabel?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" role="dialog" aria-modal="true" aria-label={title}>
      <section className="w-full max-w-md rounded-t-2xl bg-surface px-6 pb-[calc(1.5rem+var(--sab))] pt-6 text-lg text-ink">
        <div className="flex items-center gap-3">
          <span className="text-3xl" aria-hidden>
            {icon}
          </span>
          <h2 className="text-xl font-bold">{title}</h2>
        </div>
        <div className="mt-4 leading-relaxed">{children}</div>
        <button className="mt-3 min-h-touch w-full text-base text-ink-soft underline" onClick={onLater}>
          {laterLabel}
        </button>
      </section>
    </div>
  );
}

/** 버튼으로도 안 넘어가는 앱(아이폰 일부) — 주소를 복사해 사파리·크롬에 붙여 넣는다. */
const DRIVER_HOME = 'https://driver.busin.co.kr';

/** 주소 복사 — 큰 버튼 + 주소를 크게(복사가 막힌 폰은 보고 따라 친다). `primary` 면 이 시트의 주 버튼. */
function CopyAddress({ primary }: { primary?: boolean }) {
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle');
  return (
    <div className="mt-3 rounded-xl bg-canvas p-4 text-center">
      <p className="select-all text-2xl font-bold tracking-wide text-ink">{DRIVER_HOME.replace('https://', '')}</p>
      <button
        className={primary ? 'driver-btn mt-3' : 'mt-3 min-h-touch w-full rounded-xl border border-line text-lg font-semibold text-brand-700'}
        onClick={() => void copyText(DRIVER_HOME).then((ok) => setState(ok ? 'ok' : 'fail'))}
      >
        {state === 'ok' ? '✓ 주소를 복사했습니다' : '주소 복사'}
      </button>
      {state === 'fail' && <p className="mt-2 text-base text-red-700">복사가 안 되는 폰입니다. 위 주소를 직접 입력해 주세요.</p>}
    </div>
  );
}

/** 설치 문자 받기 — 번호를 넣으면 기사앱 주소를 문자(SMS)로 보낸다(`sendDriverInstallSms` · 등록된 기사 번호에만). */
function InstallSms() {
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <div className="mt-3">
      <input
        className="min-h-touch w-full rounded-xl border border-line px-4 text-xl tracking-wide"
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        placeholder="010-0000-0000"
        aria-label="휴대폰 번호"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
      />
      <Primary
        busy={busy}
        onClick={async () => {
          setBusy(true);
          setMsg(null);
          try {
            await httpsCallable<{ phone: string }, { sent: boolean }>(functions, 'sendDriverInstallSms')({ phone });
            setMsg({ ok: true, text: '문자를 보냈습니다. 문자함에서 주소를 눌러 주세요. (기사로 등록된 번호로만 갑니다)' });
          } catch (e) {
            setMsg({ ok: false, text: (e as { message?: string }).message || '문자를 보내지 못했습니다. 아래 주소 복사로 열어 주세요.' });
          } finally {
            setBusy(false);
          }
        }}
      >
        설치 문자 받기
      </Primary>
      {msg && <p className={`mt-2 text-base ${msg.ok ? 'text-brand-700' : 'text-red-700'}`}>{msg.text}</p>}
    </div>
  );
}

function Primary({ children, onClick, busy }: { children: ReactNode; onClick: () => void; busy?: boolean }) {
  return (
    <button
      className="mt-4 min-h-touch w-full rounded-xl bg-brand-600 text-lg font-bold text-white disabled:opacity-60"
      onClick={onClick}
      disabled={busy}
    >
      {busy ? '잠시만요…' : children}
    </button>
  );
}
